import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppItem } from "@/types/whatsapp";
import { toUserError } from "@/lib/api-error";

// O motivo vai como texto pro backend; o caso de longe mais comum vem
// pré-selecionado.
const MOTIVOS = [
  "Não é freelancer (fornecedor / contato de negociação)",
  "Não é freelancer (agência)",
  "Número errado ou spam",
] as const;
const MOTIVO_OUTRO = "__outro__";

interface WhatsAppIgnorarDialogProps {
  item: WhatsAppItem | null;
  onClose: () => void;
}

export function WhatsAppIgnorarDialog({ item, onClose }: WhatsAppIgnorarDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [motivoSelecionado, setMotivoSelecionado] = useState<string>(MOTIVOS[0]);
  const [complemento, setComplemento] = useState("");

  const motivoFinal =
    motivoSelecionado === MOTIVO_OUTRO
      ? complemento.trim()
      : [motivoSelecionado, complemento.trim()].filter(Boolean).join(" — ");
  const motivoValido = motivoFinal.length >= 3;

  const ignorar = useMutation({
    mutationFn: () => whatsappApi.ignorarContato(item!.conversation_id, motivoFinal),
    onSuccess: (result) => {
      toast({
        title: "Contato ignorado",
        description:
          result.itens_rejeitados > 0
            ? `As próximas sincronizações vão pular esse contato. ${result.itens_rejeitados} itens pendentes foram descartados.`
            : "As próximas sincronizações vão pular esse contato.",
      });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-run"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-run-items"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-runs"] });
      queryClient.invalidateQueries({ queryKey: ["whatsapp-contatos-ignorados"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao ignorar contato",
        description: toUserError(error).description,
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setMotivoSelecionado(MOTIVOS[0]);
    setComplemento("");
    onClose();
  };

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ignorar contato</DialogTitle>
          <DialogDescription>
            {item?.contact_name ?? item?.telefone ?? "Este contato"} deixa de ser
            capturado e processado nas próximas sincronizações (nada mais é
            extraído nem associado a freelancer), e os itens pendentes dele são
            descartados. Dá pra reativar depois nas configurações.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Select value={motivoSelecionado} onValueChange={setMotivoSelecionado}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MOTIVOS.map((motivo) => (
                <SelectItem key={motivo} value={motivo}>
                  {motivo}
                </SelectItem>
              ))}
              <SelectItem value={MOTIVO_OUTRO}>Outro motivo...</SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            placeholder={
              motivoSelecionado === MOTIVO_OUTRO
                ? "Descreva o motivo"
                : "Complemento (opcional, ex.: fornecedor de outdoor em Salvador)"
            }
            value={complemento}
            onChange={(event) => setComplemento(event.target.value)}
            rows={2}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => ignorar.mutate()}
            disabled={!motivoValido || ignorar.isPending}
            className="gap-1.5"
          >
            {ignorar.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Ignorar contato
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
